import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft,
  Check,
  Star,
  Loader2,
  HardDrive,
  Mail,
  FolderPlus,
  Zap,
  Shield,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [subscribing, setSubscribing] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // Theme classes
  const bgPrimary = isDarkMode ? 'bg-[#16162a]' : 'bg-gray-50';
  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      // Filter only active plans with price > 0
      const activePlans = response.data.filter(p => p.status === 'active' && p.monthly_price > 0);
      setPlans(activePlans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    setSubscribing(true);
    setSelectedPlanId(plan.id);
    
    try {
      // Create checkout session using the payments endpoint
      const response = await axios.post(`${API}/payments/checkout`, {
        plan_id: plan.id,
        billing_cycle: billingCycle,
        origin_url: window.location.origin,
        skip_trial: true // Always skip trial when upgrading from this page
      });
      
      if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
      const msg = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(msg);
    } finally {
      setSubscribing(false);
      setSelectedPlanId(null);
    }
  };

  const formatPrice = (plan) => {
    const price = billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price;
    return `$${price}`;
  };

  const isCurrentPlan = (plan) => {
    return user?.subscription_plan_id === plan.id;
  };

  return (
    <div className={`min-h-screen ${bgPrimary}`}>
      {/* Header */}
      <div className={`${bgSecondary} border-b ${borderColor} sticky top-0 z-10`}>
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className={textSecondary}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className={`text-xl font-bold ${textPrimary}`}>Choose Your Plan</h1>
            <div className="w-20" /> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Plan Banner */}
        {user?.subscription_status === 'trial' && (
          <div className={`mb-8 p-4 ${bgSecondary} rounded-xl border ${borderColor}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${textPrimary} font-medium flex items-center gap-2`}>
                  <Zap className="w-5 h-5 text-yellow-500" />
                  You're currently on a free trial
                </p>
                <p className={`text-sm ${textMuted}`}>
                  Upgrade now to unlock all premium features and skip the trial period
                </p>
              </div>
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                Trial
              </Badge>
            </div>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className={`inline-flex items-center ${bgSecondary} border ${borderColor} rounded-full p-1`}>
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-[#6D4AFF] text-white' 
                  : `${textMuted} hover:${textPrimary}`
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'yearly' 
                  ? 'bg-[#6D4AFF] text-white' 
                  : `${textMuted} hover:${textPrimary}`
              }`}
            >
              Yearly
              <span className="ml-2 text-xs text-green-400">Save 17%</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-10 h-10 text-[#6D4AFF] animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative ${bgSecondary} ${borderColor} ${
                  plan.is_featured ? 'border-[#6D4AFF] shadow-[0_0_30px_rgba(109,74,255,0.2)]' : ''
                } ${isCurrentPlan(plan) ? 'ring-2 ring-green-500' : ''}`}
              >
                {plan.is_featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#6D4AFF] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" /> POPULAR
                  </div>
                )}
                
                {isCurrentPlan(plan) && (
                  <div className="absolute -top-3 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    CURRENT
                  </div>
                )}

                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Crown className={`w-5 h-5 ${plan.is_featured ? 'text-[#6D4AFF]' : textMuted}`} />
                    <CardTitle className={textPrimary}>{plan.name}</CardTitle>
                  </div>
                  <CardDescription className={textMuted}>{plan.description}</CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Price */}
                  <div>
                    <span className={`text-4xl font-black ${textPrimary}`}>{formatPrice(plan)}</span>
                    <span className={textMuted}>/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  </div>

                  {/* Features */}
                  <div className="space-y-3">
                    <div className={`flex items-center gap-3 ${textSecondary}`}>
                      <HardDrive className="w-4 h-4 text-[#6D4AFF]" />
                      <span>{plan.storage_limit_gb} GB Storage</span>
                    </div>
                    <div className={`flex items-center gap-3 ${textSecondary}`}>
                      <Mail className="w-4 h-4 text-[#6D4AFF]" />
                      <span>{plan.emails_per_day} Emails/Day</span>
                    </div>
                    <div className={`flex items-center gap-3 ${textSecondary}`}>
                      <FolderPlus className="w-4 h-4 text-[#6D4AFF]" />
                      <span>{plan.custom_folders_limit} Custom Folders</span>
                    </div>
                    <div className={`flex items-center gap-3 ${textSecondary}`}>
                      <Shield className="w-4 h-4 text-[#6D4AFF]" />
                      <span>End-to-End Encryption</span>
                    </div>
                    <div className="flex items-center gap-3 text-green-400">
                      <Check className="w-4 h-4" />
                      <span>Priority Support</span>
                    </div>
                  </div>

                  {/* Subscribe Button */}
                  <Button
                    onClick={() => handleSubscribe(plan)}
                    disabled={subscribing || isCurrentPlan(plan)}
                    className={`w-full ${
                      isCurrentPlan(plan) 
                        ? 'bg-green-500/20 text-green-400 cursor-not-allowed' 
                        : 'bg-[#6D4AFF] hover:bg-[#5B3FD9]'
                    }`}
                  >
                    {subscribing && selectedPlanId === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrentPlan(plan) ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Current Plan
                      </>
                    ) : user?.subscription_status === 'trial' ? (
                      'Upgrade Now'
                    ) : (
                      'Subscribe'
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* FAQ or Additional Info */}
        <div className={`mt-12 p-6 ${bgSecondary} rounded-xl border ${borderColor}`}>
          <h3 className={`${textPrimary} font-semibold mb-4`}>Frequently Asked Questions</h3>
          <div className="space-y-4">
            <div>
              <p className={`${textPrimary} font-medium`}>Can I upgrade during my trial?</p>
              <p className={`text-sm ${textMuted}`}>
                Yes! You can upgrade to any paid plan at any time during your trial. Your trial will end immediately and you'll be charged for the selected plan.
              </p>
            </div>
            <div>
              <p className={`${textPrimary} font-medium`}>What happens after I subscribe?</p>
              <p className={`text-sm ${textMuted}`}>
                Your account will be immediately upgraded to the selected plan with all premium features unlocked. Billing will recur automatically based on your selected cycle.
              </p>
            </div>
            <div>
              <p className={`${textPrimary} font-medium`}>Can I cancel anytime?</p>
              <p className={`text-sm ${textMuted}`}>
                Yes, you can cancel your subscription at any time from your account settings. You'll continue to have access until the end of your billing period.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
